import React, { useContext } from 'react';
import { useParentSize } from '@visx/responsive';
import { ChartDimensions } from './Chart.types';

type ChartContainerContextType =
  | (ChartDimensions & { containerRef: React.RefObject<HTMLDivElement | null>; top: number; left: number })
  | undefined;

const ChartContainerContext = React.createContext<ChartContainerContextType>(undefined);

export const ChartContainer = ({
  children,
  width: widthProp,
  height: heightProp,
  ...rest
}: React.PropsWithChildren<{ width?: number; height?: number } & React.ComponentProps<'div'>>) => {
  const { parentRef: containerRef, height, width, left, top } = useParentSize<HTMLDivElement>({ debounceTime: 0 });
  const w = widthProp ?? width;
  const h = heightProp ?? height;
  const value = React.useMemo(() => ({ height: h, width: w, left, top, containerRef }), [h, w, left, top, containerRef]);

  return (
    <ChartContainerContext.Provider value={value}>
      <div
        ref={containerRef}
        style={{ width: widthProp ?? '100%', height: heightProp ?? '100%', position: 'relative', gridArea: 'chart', overflow: 'hidden' }}
        {...rest}>
        {children}
      </div>
    </ChartContainerContext.Provider>
  );
};

export const useChartContainerContext = () => {
  const ctx = useContext(ChartContainerContext);
  if (!ctx) throw new Error('useChartContainerContext must be used within a ChartContainer');
  return ctx;
};
